import React from "react";
import { 
  Building, 
  Search, 
  Bell, 
  Calendar, 
  Lock, 
  UserCheck, 
  Plus, 
  Compass, 
  Receipt,
  Menu,
  ShieldCheck
} from "lucide-react";
import { formatLojaName } from "../utils/lodgeName";

export const SisMacomTopNav = ({
  activeModule,
  currentUser,
  currentLoja,
  onOpenLoginModal,
  onOpenMyStatement,
  onOpenNewSessionModal,
  onOpenNewMemberModal,
  onOpenNewFinanceModal,
  isSidebarOpen,
  setIsSidebarOpen,
  canManage = true,
}) => {
  const getModuleName = (key) => {
    switch (key) {
      case "dashboard": return canManage ? "Master da Loja • Painel Executivo" : "Portal do Obreiro • Painel";
      case "obreiros": return "Secretaria • Quadro de Obreiros";
      case "frequencia": return "Chancelaria • Livro de Presenças & Frequência";
      case "instrucao": return "Secretaria • Câmaras de Instrução & Comissões";
      case "tesouraria": return "Tesouraria • Conta Corrente Geral & Tronco de Beneficência";
      case "centros_custo": return "Tesouraria • Centros de Custo & Orçamentos";
      case "mensalidades_desconto": return "Tesouraria • Mensalidades & Descontos de Pontualidade";
      case "perfil_loja": return "Menu da Loja • Perfil da Oficina";
      case "meu_perfil": return "Menu da Loja • Meu Perfil & Extrato Individual";
      case "agenda": return "Menu da Loja • Agenda de Sessões e Eventos";
      case "mensagens": return "Menu da Loja • Quadro de Avisos & Mensagens";
      case "arquivos": return "Menu da Loja • Arquivos em Nuvem & Pranchas";
      case "relatorios_balancete": return "Tesouraria • Balanço Consolidado";
      case "candidatos": return "Secretaria • Candidatos à Iniciação";
      case "balaustres": return "Secretaria • Balaústres (Atas das Sessões)";
      case "usuarios": return "Secretaria • Controle de Acessos e Usuários";
      case "obreiros": return "Secretaria • Membros da Loja";
      case "auditoria": return "Master da Loja • Trilha de Auditoria";
      case "datas_comemorativas": return "Chancelaria • Datas Comemorativas";
      case "presenca_qr": return "Chancelaria • Presença por QR Code";
      case "templates_balaustre": return "Chancelaria • Modelos de Balaústres";
      case "loja_settings": return "Master da Loja • Papel Timbrado & Configurações da Loja";
      default: return "Lojas Integradas • Gestão Maçônica";
    }
  };

  return (
    <header className="h-20 bg-[#0B111E]/95 border-b border-[#D4AF37]/20 px-4 sm:px-8 flex items-center justify-between sticky top-0 z-30 backdrop-blur-xl">
      
      {/* Left: Mobile Toggle & Page Breadcrumb */}
      <div className="flex items-center space-x-3">
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="p-2 rounded-lg bg-[#131C2E] text-slate-400 hover:text-white lg:hidden border border-slate-700"
        >
          <Menu className="w-5 h-5" />
        </button>

        <div>
          <div className="flex items-center space-x-2 text-xs font-semibold uppercase tracking-wider text-[#D4AF37]">
            <Compass className="w-3.5 h-3.5" />
            <span data-testid="topnav-loja-name">
              {currentLoja
                ? formatLojaName(currentLoja, { withArls: true })
                : "Loja Maçônica"}
            </span>
          </div>
          <h1 className="text-sm sm:text-base font-serif font-bold text-white leading-tight">
            {getModuleName(activeModule)}
          </h1>
        </div>
      </div>

      {/* Right: Quick Action Buttons & User Profile */}
      <div className="flex items-center space-x-3">
        
        {/* Quick Add Session Shortcut — só nas pastas de Secretaria/Presidência */}
        {canManage && ["dashboard", "obreiros", "frequencia", "balaustres", "templates_balaustre"].includes(activeModule) && (
          <button
            data-testid="topnav-btn-new-session"
            onClick={onOpenNewSessionModal}
            className="hidden sm:flex items-center space-x-1.5 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-3.5 py-2 rounded-xl text-xs uppercase tracking-wider transition-all shadow-[0_0_15px_rgba(212,175,55,0.25)]"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Nova Sessão</span>
          </button>
        )}

        {/* Quick Brother Statement */}
        {currentUser?.member_id && (
          <button
            data-testid="topnav-btn-my-statement"
            onClick={() => onOpenMyStatement(currentUser.member_id)}
            className="flex items-center space-x-1.5 bg-[#131C2E] hover:bg-[#1E293B] text-[#D4AF37] border border-[#D4AF37]/40 font-semibold px-3 py-2 rounded-xl text-xs transition-all"
          >
            <Receipt className="w-3.5 h-3.5" />
            <span className="hidden md:inline">Extrato Individual</span>
          </button>
        )}

        {/* Profile Switcher */}
        <button
          data-testid="topnav-btn-switch-user"
          onClick={onOpenLoginModal}
          className="flex items-center space-x-2 bg-[#131C2E] hover:bg-[#1E293B] text-slate-200 border border-slate-700 px-3 py-2 rounded-xl text-xs font-semibold transition-all"
        >
          <Lock className="w-3.5 h-3.5 text-[#D4AF37]" />
          <span className="hidden lg:inline">{currentUser?.name || "Entrar"}</span>
          <span className="text-[10px] bg-[#D4AF37]/20 text-[#D4AF37] px-2 py-0.5 rounded font-semibold whitespace-nowrap">
            {(() => {
              if (!currentUser) return "—";
              if (currentUser.is_master_loja || currentUser.role === "master_loja") return "Master da Loja";
              const map = {
                super_admin: "Super-Admin",
                veneravel: "Venerável",
                secretaria: "Secretaria",
                tesoureiro: "Tesouraria",
                chanceler: "Chancelaria",
                irmao: "Irmão",
              };
              return map[currentUser.role] || currentUser.role || "Usuário";
            })()}
          </span>
        </button>

      </div>

    </header>
  );
};
